import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Box,
  Button,
  Snackbar,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import {
  ViewCompact as ClassicIcon,
  ViewModule as ModernIcon,
} from "@material-ui/icons";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  description: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  layoutOption: {
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.action.hover,
    },
  },
  layoutOptionSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + "20",
  },
  layoutIcon: {
    fontSize: 48,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  layoutTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  layoutDescription: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  previewContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
  },
  previewImage: {
    width: "100%",
    height: 120,
    backgroundColor: theme.palette.grey[200],
    borderRadius: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
  },
  saveButton: {
    marginTop: theme.spacing(2),
  },
  chip: {
    marginLeft: theme.spacing(1),
    fontSize: "0.7rem",
    height: 20,
  },
}));

const LayoutSelector = () => {
  const classes = useStyles();
  const { get: getSetting, update: updateSetting } = useCompanySettings();
  const [layoutType, setLayoutType] = useState("modern");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const fetchLayoutSetting = async () => {
      try {
        const setting = await getSetting({ column: "layoutType" });
        if (setting && setting.layoutType) {
          setLayoutType(setting.layoutType);
          localStorage.setItem("preferredLayout", setting.layoutType);
        }
      } catch (error) {
        console.log("Erro ao buscar configuração de layout:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLayoutSetting();
  }, [getSetting]);

  const handleLayoutChange = (event) => {
    setLayoutType(event.target.value);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSetting({
        column: "layoutType",
        data: layoutType,
      });
      localStorage.setItem("preferredLayout", layoutType);
      setSnackbar({
        open: true,
        message: "Layout atualizado com sucesso! Recarregue a página para aplicar as mudanças.",
        severity: "success",
      });
      
      // Recarrega a página após 2 segundos
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error("Erro ao salvar configuração de layout:", error);
      setSnackbar({
        open: true,
        message: "Erro ao salvar configuração de layout",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Paper className={classes.root}>
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={classes.root}>
      <Typography variant="h6" className={classes.title}>
        🎨 {i18n.t("settings.layout.title") || "Layout do Sistema"}
      </Typography>
      <Typography variant="body2" className={classes.description}>
        {i18n.t("settings.layout.description") || "Escolha o estilo de layout que prefere para o sistema. O layout moderno oferece uma navegação mais rápida com menu horizontal."}
      </Typography>

      <FormControl component="fieldset" fullWidth>
        <RadioGroup
          value={layoutType}
          onChange={handleLayoutChange}
        >
          <Grid container spacing={3}>
            {/* Layout Clássico */}
            <Grid item xs={12} md={6}>
              <Box
                className={`${classes.layoutOption} ${
                  layoutType === "classic" ? classes.layoutOptionSelected : ""
                }`}
                onClick={() => setLayoutType("classic")}
              >
                <FormControlLabel
                  value="classic"
                  control={<Radio color="primary" />}
                  label=""
                  style={{ position: "absolute", opacity: 0 }}
                />
                <Box textAlign="center">
                  <ClassicIcon className={classes.layoutIcon} />
                  <Typography className={classes.layoutTitle}>
                    {i18n.t("settings.layout.classic.title") || "Layout Clássico"}
                  </Typography>
                  <Typography className={classes.layoutDescription}>
                    {i18n.t("settings.layout.classic.description") || "Menu lateral tradicional, ideal para quem prefere navegação vertical."}
                  </Typography>
                  <Box className={classes.previewContainer}>
                    <Box className={classes.previewImage}>
                      <Box display="flex" width="100%" height="100%">
                        <Box 
                          width="20%" 
                          bgcolor="primary.main" 
                          borderRadius="4px 0 0 4px"
                          display="flex"
                          alignItems="flex-start"
                          justifyContent="center"
                          pt={1}
                        >
                          <Typography variant="caption" style={{ color: "white", fontSize: "0.6rem" }}>
                            Menu
                          </Typography>
                        </Box>
                        <Box width="80%" bgcolor="grey.100" borderRadius="0 4px 4px 0" p={1}>
                          <Box bgcolor="grey.300" height="10%" mb={0.5} borderRadius="2px" />
                          <Box bgcolor="grey.200" height="85%" borderRadius="2px" />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Layout Moderno */}
            <Grid item xs={12} md={6}>
              <Box
                className={`${classes.layoutOption} ${
                  layoutType === "modern" ? classes.layoutOptionSelected : ""
                }`}
                onClick={() => setLayoutType("modern")}
              >
                <FormControlLabel
                  value="modern"
                  control={<Radio color="primary" />}
                  label=""
                  style={{ position: "absolute", opacity: 0 }}
                />
                <Box textAlign="center">
                  <ModernIcon className={classes.layoutIcon} />
                  <Typography className={classes.layoutTitle}>
                    {i18n.t("settings.layout.modern.title") || "Layout Moderno"}
                    <span style={{ 
                      marginLeft: 8, 
                      fontSize: "0.65rem", 
                      backgroundColor: "#4caf50", 
                      color: "white",
                      padding: "2px 6px",
                      borderRadius: 4,
                      fontWeight: 500
                    }}>
                      NOVO
                    </span>
                  </Typography>
                  <Typography className={classes.layoutDescription}>
                    {i18n.t("settings.layout.modern.description") || "Menu horizontal no topo, design moderno e navegação mais rápida."}
                  </Typography>
                  <Box className={classes.previewContainer}>
                    <Box className={classes.previewImage}>
                      <Box display="flex" flexDirection="column" width="100%" height="100%">
                        <Box 
                          height="15%" 
                          bgcolor="primary.main" 
                          borderRadius="4px 4px 0 0"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Typography variant="caption" style={{ color: "white", fontSize: "0.6rem" }}>
                            Menu Horizontal
                          </Typography>
                        </Box>
                        <Box height="85%" bgcolor="grey.100" borderRadius="0 0 4px 4px" p={1}>
                          <Box bgcolor="grey.200" height="100%" borderRadius="2px" />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </RadioGroup>
      </FormControl>

      <Box display="flex" justifyContent="flex-end" className={classes.saveButton}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            i18n.t("settings.layout.save") || "Salvar e Aplicar"
          )}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default LayoutSelector;
